import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { gsap } from 'gsap';
import {
  ArrowForward,
  Check,
  CloudOutlined,
  Download,
  FolderOutlined,
  HeadphonesOutlined,
  NorthEast,
  Pause,
  PlayArrow,
  Replay,
  Sync,
  Upload,
} from '@mui/icons-material';
import { dawLogos } from '@/theme';
import { brand } from '@/theme/foundations/brand';
import './handoff.css';

export const HANDOFF_STEPS = [
  {
    title: 'Make your changes',
    detail:
      'Start in your own session folder. Review what changed before uploading.',
    time: 0,
  },
  {
    title: 'Create a version',
    detail: 'Add a commit message and upload the new and updated files.',
    time: 2,
  },
  {
    title: 'Share the checkpoint',
    detail:
      'The version is available to collaborators who have access to this project.',
    time: 6.5,
  },
  {
    title: 'Download on their machine',
    detail:
      'Your collaborator chooses Download and a destination on their computer.',
    time: 9,
  },
  {
    title: 'Pick up where you left off',
    detail:
      'They open the downloaded session in their installed DAW and keep creating.',
    time: 13.5,
  },
];

function Cursor({ className }: { className: string }) {
  return (
    <svg
      className={`hf-cursor ${className}`}
      viewBox="0 0 24 30"
      aria-hidden="true"
    >
      <path
        d="M3 2v23l6-7 5 10 4-2-5-9 9-1Z"
        fill={brand.handoffArtwork.cursorFill}
        stroke={brand.handoffArtwork.cursorOutline}
        strokeWidth="1.5"
      />
    </svg>
  );
}

function SessionTracks({ playing = false }: { playing?: boolean }) {
  return (
    <div
      className={`hf-tracks${playing ? ' hf-tracks-playing' : ''}`}
      aria-hidden="true"
    >
      <div className="hf-ruler">
        <span>1</span>
        <span>9</span>
        <span>17</span>
        <span>25</span>
        <span>33</span>
      </div>
      {['DRUMS', 'BASS', 'KEYS', 'VOCAL'].map((label, row) => (
        <div className={`hf-track hf-track-${row}`} key={label}>
          <span>{label}</span>
          <div className="hf-audio-clip">
            {Array.from({ length: 36 }, (_, i) => (
              <i
                key={i}
                style={{ height: `${15 + ((i * 19 + row * 23) % 70)}%` }}
              />
            ))}
          </div>
        </div>
      ))}
      <div className="hf-playhead" />
    </div>
  );
}

export function LandingHandoff() {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [phase, setPhase] = useState(reducedMotion ? 4 : 0);
  const [paused, setPaused] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const root = useRef<HTMLElement>(null);
  const timeline = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.15 }
    );
    if (root.current) observer.observe(root.current);
    const visibility = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);

  useLayoutEffect(() => {
    const context = gsap.context(() => {
      const tl = gsap.timeline({ paused: true, repeat: -1 });
      timeline.current = tl;
      tl.set(
        '.hf-upload-fill, .hf-download-fill, .hf-story-progress',
        { scaleX: 0 },
        0
      )
        .set('.hf-send-packet, .hf-receive-packet', { opacity: 0, x: 0 }, 0)
        .set('.hf-cursor-left', { opacity: 0, x: 22, y: 25 }, 0)
        .set('.hf-cursor-right', { opacity: 0, x: 20, y: 30 }, 0)
        .set('.hf-vault-halo', { opacity: 0.15, scale: 0.92 }, 0)
        .set('.hf-version-stamp', { opacity: 0, y: 6 }, 0)
        .set('.hf-daw-view', { opacity: 0, y: 12 }, 0)
        .set('.hf-receiver-project', { opacity: 1, y: 0 }, 0)
        .set('.hf-playhead', { left: '18%' }, 0)
        .call(() => setPhase(0), [], 0)
        .to(
          '.hf-cursor-left',
          { opacity: 1, x: 0, y: 0, duration: 0.8, ease: 'power3.out' },
          0.8
        )
        .to(
          '.hf-upload-button',
          { scale: 0.96, duration: 0.12, yoyo: true, repeat: 1 },
          1.7
        )
        .call(() => setPhase(1), [], 2)
        .to('.hf-cursor-left', { opacity: 0, duration: 0.3 }, 2.15)
        .to(
          '.hf-upload-fill',
          { scaleX: 1, duration: 3.8, ease: 'power1.inOut' },
          2.1
        )
        .to('.hf-vault-halo', { opacity: 0.8, scale: 1.1, duration: 1.4 }, 2.3)
        .to('.hf-send-packet', { opacity: 1, duration: 0.15 }, 2.4)
        .to(
          '.hf-send-packet',
          { x: 68, duration: 0.95, stagger: 0.3, repeat: 2, ease: 'none' },
          2.4
        )
        .to('.hf-send-packet', { opacity: 0, duration: 0.3 }, 5.9)
        .call(() => setPhase(2), [], 6.5)
        .to(
          '.hf-version-stamp',
          { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' },
          6.5
        )
        .to('.hf-vault-halo', { scale: 1, opacity: 0.5, duration: 0.7 }, 6.5)
        .to(
          '.hf-cursor-right',
          { opacity: 1, x: 0, y: 0, duration: 0.8, ease: 'power3.out' },
          7.6
        )
        .to(
          '.hf-download-button',
          { scale: 0.96, duration: 0.12, yoyo: true, repeat: 1 },
          8.65
        )
        .call(() => setPhase(3), [], 9)
        .to('.hf-cursor-right', { opacity: 0, duration: 0.3 }, 9.2)
        .to(
          '.hf-download-fill',
          { scaleX: 1, duration: 3.5, ease: 'power1.inOut' },
          9.2
        )
        .to('.hf-receive-packet', { opacity: 1, duration: 0.15 }, 9.3)
        .to(
          '.hf-receive-packet',
          { x: 68, duration: 0.9, stagger: 0.3, repeat: 2, ease: 'none' },
          9.3
        )
        .to('.hf-receive-packet', { opacity: 0, duration: 0.3 }, 12.5)
        .to('.hf-open-action', { opacity: 1, duration: 0.3 }, 12.7)
        .call(() => setPhase(4), [], 13.5)
        .to('.hf-receiver-project', { opacity: 0, y: -10, duration: 0.5 }, 13.5)
        .to(
          '.hf-daw-view',
          { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' },
          13.8
        )
        .to(
          '.hf-daw-view .hf-playhead',
          { left: '94%', duration: 4.5, ease: 'none' },
          14.4
        )
        .to('.hf-vault-halo', { opacity: 0.2, duration: 2 }, 17)
        .to(
          '.hf-story-progress',
          { scaleX: 1, duration: 20.5, ease: 'none' },
          0
        );
      if (reducedMotion) tl.seek(19, false).pause();
    }, root);
    return () => {
      context.revert();
      timeline.current = null;
    };
  }, [reducedMotion]);

  useEffect(() => {
    const shouldPlay = !paused && visible && pageVisible && !reducedMotion;
    if (shouldPlay) timeline.current?.play();
    else timeline.current?.pause();
  }, [paused, visible, pageVisible, reducedMotion]);

  const choose = (index: number) => {
    setPaused(true);
    timeline.current
      ?.seek(index === 4 ? 16 : HANDOFF_STEPS[index].time + 0.6, false)
      .pause();
    setPhase(index);
  };
  const replay = () => {
    if (reducedMotion) {
      choose(0);
      return;
    }
    setPaused(false);
    setPhase(0);
    timeline.current?.restart();
  };

  return (
    <section
      className="hf-section"
      id="lp-handoff"
      ref={root}
      data-phase={phase}
      aria-labelledby="hf-heading"
    >
      <div className="hf-heading-row">
        <div>
          <p className="hf-eyebrow">
            CREATIVITY DOESN’T HAVE TO STAY IN ONE ROOM
          </p>
          <h2 id="hf-heading">
            From your studio.
            <br />
            <span>To theirs.</span>
          </h2>
        </div>
        <p className="hf-intro">
          The session you just worked on.
          <br />
          Ready for the person you’re making it with.
          <br />
          <strong>Unlimited collaborators. Invite your whole team.</strong>
        </p>
      </div>
      <div className="hf-stage">
        <div className="hf-stage-reflection" aria-hidden="true" />
        <div className="hf-stage-topline">
          <span>
            <i /> ONE SHARED PROJECT. TWO CREATIVE SPACES.
          </span>
          <span>ILLUSTRATED WORKFLOW</span>
        </div>
        <div className="hf-machines">
          <div className="hf-machine hf-sender">
            <div className="hf-person">
              <span className="hf-avatar">Y</span>
              <div>
                <strong>You</strong>
                <span>Your studio · desktop app</span>
              </div>
              <span className="hf-machine-tag">01</span>
            </div>
            <div className="hf-screen">
              <div className="hf-window-bar">
                <span className="hf-window-dots">● ● ●</span>
                <span>StudioVault</span>
                <Sync />
              </div>
              <div className="hf-screen-content">
                <div className="hf-project-title">
                  <img src={dawLogos.Ableton} alt="Ableton Live" />
                  <div>
                    <strong>Nightdrive</strong>
                    <span>Local project folder</span>
                  </div>
                </div>
                <SessionTracks />
                <div className="hf-file-change">
                  <span>UPDATED</span> Nightdrive.als <Check />
                </div>
                <div className="hf-file-change">
                  <span>NEW</span> Samples / bridge.wav <Check />
                </div>
                <div className="hf-commit">
                  <span>COMMIT MESSAGE</span>
                  <strong>New bridge. Ready for your vocals.</strong>
                </div>
                <div className="hf-simulated-button hf-upload-button">
                  <Upload />
                  {phase === 0
                    ? 'Create version'
                    : phase === 1
                      ? 'Uploading changes…'
                      : 'Version uploaded'}
                  <Cursor className="hf-cursor-left" />
                </div>
                <div className="hf-transfer-track" aria-hidden="true">
                  <div className="hf-upload-fill" />
                </div>
                <p className="hf-window-status">
                  {phase < 2
                    ? 'Only new and updated files are uploaded.'
                    : 'v15 · New bridge. Ready for your vocals.'}
                </p>
              </div>
            </div>
            <div className="hf-laptop-base" aria-hidden="true" />
          </div>
          <div className="hf-bridge" aria-hidden="true">
            <svg className="hf-routes" viewBox="0 0 200 200">
              <defs>
                <linearGradient id="hf-route-gradient">
                  <stop
                    stopColor={brand.handoffArtwork.routeStart}
                    stopOpacity=".15"
                  />
                  <stop
                    offset=".5"
                    stopColor={brand.handoffArtwork.routeMiddle}
                  />
                  <stop
                    offset="1"
                    stopColor={brand.handoffArtwork.routeEnd}
                    stopOpacity=".15"
                  />
                </linearGradient>
              </defs>
              <path
                d="M0 100H200"
                stroke="url(#hf-route-gradient)"
                strokeWidth="1"
              />
              <path
                d="M0 94H200M0 106H200"
                stroke={brand.handoffArtwork.routeOutline}
                strokeOpacity=".035"
              />
              {[0, 1, 2].map((i) => (
                <g key={i}>
                  <circle
                    className="hf-send-packet"
                    cx={i * 7}
                    cy="100"
                    r="2"
                    fill={brand.handoffArtwork.sendPacket}
                  />
                  <circle
                    className="hf-receive-packet"
                    cx={118 + i * 7}
                    cy="100"
                    r="2"
                    fill={brand.handoffArtwork.receivePacket}
                  />
                </g>
              ))}
            </svg>
            <div className="hf-vault-halo" />
            <div className="hf-vault">
              <CloudOutlined />
              <span>StudioVault</span>
            </div>
            <div className="hf-version-stamp">
              <Check /> v15 available
            </div>
            <div className="hf-bridge-label">
              {phase < 2
                ? 'YOUR SHARED VAULT'
                : phase < 4
                  ? 'SAME PROJECT. NEXT VERSION.'
                  : 'KEEP THE IDEAS MOVING.'}
            </div>
          </div>
          <div className="hf-machine hf-receiver">
            <div className="hf-person">
              <span className="hf-avatar collaborator">C</span>
              <div>
                <strong>Your collaborator</strong>
                <span>Their studio · desktop app</span>
              </div>
              <span className="hf-machine-tag">02</span>
            </div>
            <div className="hf-screen">
              <div className="hf-window-bar">
                <span className="hf-window-dots">● ● ●</span>
                <span>{phase === 4 ? 'Their DAW' : 'StudioVault'}</span>
                {phase === 4 ? <HeadphonesOutlined /> : <Download />}
              </div>
              <div className="hf-receiver-body">
                <div className="hf-receiver-project" aria-hidden={phase === 4}>
                  <div className="hf-project-title">
                    <img src={dawLogos.Ableton} alt="Ableton Live" />
                    <div>
                      <strong>Nightdrive</strong>
                      <span>Shared with you</span>
                    </div>
                  </div>
                  <div className="hf-shared-version">
                    <span className="hf-version-number">
                      v{phase < 2 ? '14' : '15'}
                    </span>
                    <span className="hf-latest">LATEST VERSION</span>
                    <h3>
                      {phase < 2
                        ? 'Vocal comp + new bridge'
                        : 'New bridge. Ready for your vocals.'}
                    </h3>
                    <p>
                      {phase < 2
                        ? 'A shared project, with its history intact.'
                        : 'Your collaborator’s checkpoint is ready.'}
                    </p>
                  </div>
                  <div className="hf-destination">
                    <FolderOutlined />
                    <span>Choose a local destination</span>
                    <span>↗</span>
                  </div>
                  <div className="hf-simulated-button hf-download-button">
                    <Download />
                    {phase < 3
                      ? 'Download version'
                      : phase === 3
                        ? 'Downloading v15…'
                        : 'Downloaded'}
                    <Cursor className="hf-cursor-right" />
                  </div>
                  <div className="hf-transfer-track" aria-hidden="true">
                    <div className="hf-download-fill" />
                  </div>
                  <p className="hf-window-status">
                    {phase < 3
                      ? 'Download when you’re ready to work.'
                      : 'Bringing the session to this computer.'}
                  </p>
                </div>
                <div className="hf-daw-view" aria-hidden={phase !== 4}>
                  <div className="hf-daw-ready">
                    <span>
                      <Check /> v15 downloaded
                    </span>
                    <span className="hf-open-action">
                      Open in DAW <NorthEast />
                    </span>
                  </div>
                  <div className="hf-project-title">
                    <img src={dawLogos.Ableton} alt="Ableton Live" />
                    <div>
                      <strong>Nightdrive.als</strong>
                      <span>Opened on their computer</span>
                    </div>
                  </div>
                  <SessionTracks playing />
                  <div className="hf-daw-transport">
                    <PlayArrow />
                    <span>SESSION READY</span>
                    <span>v15</span>
                  </div>
                  <p className="hf-creative-note">
                    Your bridge.
                    <br />
                    <span>Their next idea.</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="hf-laptop-base" aria-hidden="true" />
          </div>
        </div>
        <div className="hf-story">
          <div className="hf-step-count">
            0{phase + 1}
            <span> / 05</span>
          </div>
          <div className="hf-story-copy" aria-live={paused ? 'polite' : 'off'}>
            <h3>{HANDOFF_STEPS[phase].title}</h3>
            <p>{HANDOFF_STEPS[phase].detail}</p>
          </div>
          <div className="hf-playback">
            <button
              onClick={replay}
              aria-label="Replay collaboration animation"
            >
              <Replay />
            </button>
            {!reducedMotion && (
              <button
                onClick={() => setPaused(!paused)}
                aria-label={
                  paused
                    ? 'Play collaboration animation'
                    : 'Pause collaboration animation'
                }
              >
                {paused ? <PlayArrow /> : <Pause />}
              </button>
            )}
          </div>
        </div>
        <div className="hf-story-track" aria-hidden="true">
          <div className="hf-story-progress" />
        </div>
      </div>
      <div className="hf-step-nav" aria-label="Collaboration animation steps">
        {HANDOFF_STEPS.map((step, index) => (
          <button
            key={step.title}
            onClick={() => choose(index)}
            aria-pressed={phase === index}
          >
            <span>0{index + 1}</span>
            {step.title}
            <ArrowForward />
          </button>
        ))}
      </div>
      <p className="hf-disclosure">
        Illustrative demo, not live co-editing. Your collaborator needs project
        access, the desktop app and the required DAW/plugins. Transfers and
        opening the session are user-initiated; timing is illustrative. No audio
        plays.
      </p>
    </section>
  );
}
